import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Check, AlertCircle } from 'lucide-react';
import { useBrand } from '../../contexts/BrandContext';
import { forecastService, ForecastScenario } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';

interface ScenarioManagerProps {
  onClose: () => void;
  onScenarioSelected?: (scenarioId: string) => void;
}

const ScenarioManager: React.FC<ScenarioManagerProps> = ({ onClose, onScenarioSelected }) => {
  const { currentBrand } = useBrand();
  const { showToast } = useToast();
  const [scenarios, setScenarios] = useState<ForecastScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewScenarioForm, setShowNewScenarioForm] = useState(false);
  const [newScenario, setNewScenario] = useState({
    scenario_name: '',
    description: '',
    churn_rate_annual: 10,
  });

  useEffect(() => {
    if (currentBrand) {
      loadScenarios();
    }
  }, [currentBrand]);

  const loadScenarios = async () => {
    if (!currentBrand) return;

    try {
      setLoading(true);
      const data = await forecastService.getScenarios(currentBrand.id);
      setScenarios(data);
    } catch (error) {
      showToast('Failed to load scenarios', 'error');
      console.error('Error loading scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const annualToMonthlyChurnRate = (annualRate: number): number => {
    return 1 - Math.pow(1 - annualRate / 100, 1 / 12);
  };

  const monthlyToAnnualChurnRate = (monthlyRate: number): number => {
    return (1 - Math.pow(1 - monthlyRate, 12)) * 100;
  };

  const handleCreateScenario = async () => {
    if (!currentBrand || !newScenario.scenario_name.trim()) {
      showToast('Please enter a scenario name', 'error');
      return;
    }

    try {
      const monthlyRate = annualToMonthlyChurnRate(newScenario.churn_rate_annual);

      await forecastService.createScenario({
        brand_id: currentBrand.id,
        scenario_name: newScenario.scenario_name.trim(),
        description: newScenario.description.trim() || undefined,
        churn_rate_monthly: monthlyRate,
        is_default: false,
      });

      showToast('Scenario created successfully', 'success');
      setShowNewScenarioForm(false);
      setNewScenario({
        scenario_name: '',
        description: '',
        churn_rate_annual: 10,
      });
      loadScenarios();
    } catch (error) {
      showToast('Failed to create scenario', 'error');
      console.error('Error creating scenario:', error);
    }
  };

  const handleSetDefault = async (scenarioId: string) => {
    try {
      await forecastService.updateScenario(scenarioId, { is_default: true });
      showToast('Default scenario updated', 'success');
      loadScenarios();
    } catch (error) {
      showToast('Failed to update default scenario', 'error');
      console.error('Error updating scenario:', error);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    if (!confirm('Are you sure you want to delete this scenario?')) {
      return;
    }

    try {
      await forecastService.deleteScenario(scenarioId);
      showToast('Scenario deleted successfully', 'success');
      loadScenarios();
    } catch (error) {
      showToast('Failed to delete scenario', 'error');
      console.error('Error deleting scenario:', error);
    }
  };

  const handleSelectScenario = (scenarioId: string) => {
    if (onScenarioSelected) {
      onScenarioSelected(scenarioId);
    }
    onClose();
  };

  if (!currentBrand) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Forecast Scenarios</h2>
            <p className="text-sm text-gray-600 mt-1">
              Model different churn rates to explore revenue projections
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500">Loading scenarios...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">About Churn Rate Scenarios</p>
                    <p>
                      Churn rate represents the percentage of recurring revenue lost over time due to customer
                      cancellations. These scenarios help you plan for different retention outcomes by showing
                      how your revenue might decline without new customer acquisition.
                    </p>
                  </div>
                </div>
              </div>

              {scenarios.map((scenario) => {
                const annualRate = monthlyToAnnualChurnRate(scenario.churn_rate_monthly);
                const isBaseline = scenario.churn_rate_monthly === 0;

                return (
                  <div
                    key={scenario.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      scenario.is_default
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {scenario.scenario_name}
                          </h3>
                          {scenario.is_default && (
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              Default
                            </span>
                          )}
                          {isBaseline && (
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                              Baseline
                            </span>
                          )}
                        </div>
                        {scenario.description && (
                          <p className="text-sm text-gray-600 mt-1">{scenario.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3">
                          <div>
                            <p className="text-xs text-gray-500">Monthly Churn</p>
                            <p className="text-lg font-bold text-gray-900">
                              {(scenario.churn_rate_monthly * 100).toFixed(2)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Annual Churn</p>
                            <p className="text-lg font-bold text-gray-900">
                              {annualRate.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {!scenario.is_default && (
                          <button
                            onClick={() => handleSetDefault(scenario.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Set as default"
                          >
                            <Check className="w-5 h-5 text-gray-600" />
                          </button>
                        )}
                        {!isBaseline && (
                          <button
                            onClick={() => handleDeleteScenario(scenario.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete scenario"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        )}
                        {onScenarioSelected && (
                          <button
                            onClick={() => handleSelectScenario(scenario.id)}
                            className="px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            Use This
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {showNewScenarioForm ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Scenario</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scenario Name
                      </label>
                      <input
                        type="text"
                        value={newScenario.scenario_name}
                        onChange={(e) => setNewScenario({ ...newScenario, scenario_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Aggressive Growth"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (Optional)
                      </label>
                      <textarea
                        value={newScenario.description}
                        onChange={(e) => setNewScenario({ ...newScenario, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={2}
                        placeholder="Describe the assumptions for this scenario"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annual Churn Rate: {newScenario.churn_rate_annual}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        step="1"
                        value={newScenario.churn_rate_annual}
                        onChange={(e) => setNewScenario({ ...newScenario, churn_rate_annual: parseFloat(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0% (No Churn)</span>
                        <span>50% (High Churn)</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Monthly: {(annualToMonthlyChurnRate(newScenario.churn_rate_annual) * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleCreateScenario}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Create Scenario
                    </button>
                    <button
                      onClick={() => {
                        setShowNewScenarioForm(false);
                        setNewScenario({
                          scenario_name: '',
                          description: '',
                          churn_rate_annual: 10,
                        });
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewScenarioForm(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-gray-600 hover:text-blue-600"
                >
                  <Plus className="w-5 h-5" />
                  Create Custom Scenario
                </button>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioManager;
